import { areArrayEquals, objectEntries, type ReadonlyRecord } from '../../../utils'

import type { CybusDetailedMapping, CybusPubSub, TopicSubscriptionError, TopicSubscriptionResponse } from '../../../domain'

/**
 * This is a utility class that helps map subscription errors
 * And spits out updated mappers
 */
export class TopicSubscriptionErrorMapper {
    /** Auxiliary flag to detect changes during remapping */
    private hasChanges: boolean = false

    private readonly errorsByTopic: ReadonlyRecord<string, TopicSubscriptionError>

    constructor (response: TopicSubscriptionResponse) {
        this.errorsByTopic = objectEntries(response).reduce<ReadonlyRecord<string, TopicSubscriptionError>>(
            (r, [responseType, topics]) => (responseType === 'subscribed' ? r : topics.reduce((r, topic) => ({ ...r, [topic]: responseType }), r)),
            {}
        )
    }

    private remapSubscriptionError (topics: readonly string[], original: ReadonlySet<TopicSubscriptionError>): Set<TopicSubscriptionError> {
        const newErrors = topics.reduce((errors, topic) => {
            const error = this.errorsByTopic[topic]

            if (error) {
                errors.add(error)
            }

            return errors
        }, new Set<TopicSubscriptionError>())

        this.hasChanges = this.hasChanges || !areArrayEquals(Array.from(newErrors), Array.from(original), { sort: false })

        return newErrors
    }

    /**
     * @returns null if the mapping has had no changes
     */
    remapWithErrors (mapping: CybusDetailedMapping): CybusDetailedMapping | null {
        /** Reset tracking */
        this.hasChanges = false

        const copy: CybusDetailedMapping = {
            ...mapping,
            entries: mapping.entries.map((entry) => ({
                ...entry,
                subscribe: entry.subscribe.map<CybusPubSub>((subscribe) => ({
                    ...subscribe,
                    subscriptionErrors: this.remapSubscriptionError(subscribe.topics, subscribe.subscriptionErrors),
                })),
                publish: {
                    ...entry.publish,
                    subscriptionErrors: this.remapSubscriptionError(entry.publish.topics, entry.publish.subscriptionErrors),
                },
            })),
        }

        return this.hasChanges ? copy : null
    }
}
